import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import API, { graphqlOperation } from '@aws-amplify/api-graphql';
import { Auth } from 'aws-amplify';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface PriceListModel {
  id?: string;
  productCode?: string;
  barcode?: string;
  productName: string;
  unit?: string;
  retailPrice: number;
  selected?: boolean;
  clientID?: string;
  searchKeyword?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** One response from the chunked bulkPriceListImport mutation. */
export interface PriceListImportProgress {
  result: string;
  message?: string;
  phase?: 'CLEARING' | 'IMPORTING' | 'DONE' | 'FAILED';
  totalRows?: number;
  processedRows?: number;
  insertedRows?: number;
  failedRows?: number;
  deletedRows?: number;
  nextOffset?: number;
  done?: boolean;
}

const ITEM_FIELDS = `
  id
  productCode
  barcode
  productName
  unit
  retailPrice
  clientID
  createdAt
  updatedAt
`;

@Injectable({
  providedIn: 'root',
})
export class PriceListDataService {
  constructor(private snackBar: MatSnackBar) {}

  private PRICE_LIST_DATA = new BehaviorSubject<PriceListModel[]>([]);
  private dataStore: { items: PriceListModel[] } = { items: [] };
  readonly items = this.PRICE_LIST_DATA.asObservable();
  private nextToken: string | undefined;

  resetData() {
    this.dataStore.items = [];
    this.PRICE_LIST_DATA.next([]);
  }

  async listByClientID(
    clientID: string,
    filter?: any,
    limit?: number,
    nextToken?: string
  ): Promise<{ items: PriceListModel[]; nextToken: string | null }> {
    const statement = `query GetPriceListByClientID($clientID: ID!, $filter: ModelPriceListFilterInput, $limit: Int, $nextToken: String) {
      getPriceListByClientID(clientID: $clientID, filter: $filter, limit: $limit, nextToken: $nextToken) {
        items {${ITEM_FIELDS}}
        nextToken
      }
    }`;
    const gqlAPIServiceArguments: any = {
      clientID,
      limit: limit || 50,
    };
    if (filter) gqlAPIServiceArguments.filter = filter;
    if (nextToken) gqlAPIServiceArguments.nextToken = nextToken;

    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return response.data.getPriceListByClientID;
  }

  /**
   * Every row for a client, following pagination to the end.
   *
   * Only `barcode`, `productCode`, `productName` and `retailPrice` are selected
   * because the sole caller is the MPC lookup in the invoice preview, and a real
   * price list runs to tens of thousands of rows — pulling the full record would
   * roughly double the payload for no benefit.
   */
  async listAllForLookup(clientID: string): Promise<PriceListModel[]> {
    const statement = `query GetPriceListByClientID($clientID: ID!, $limit: Int, $nextToken: String) {
      getPriceListByClientID(clientID: $clientID, limit: $limit, nextToken: $nextToken) {
        items {
          barcode
          productCode
          productName
          retailPrice
        }
        nextToken
      }
    }`;

    let allItems: PriceListModel[] = [];
    let token: string | null = null;
    do {
      const vars: any = { clientID, limit: 10000 };
      if (token) vars.nextToken = token;
      const res = (await API.graphql(graphqlOperation(statement, vars))) as any;
      const page = res?.data?.getPriceListByClientID;
      allItems = allItems.concat(page?.items ?? []);
      token = page?.nextToken ?? null;
    } while (token);
    return allItems;
  }

  async createPriceList(input: PriceListModel): Promise<PriceListModel> {
    const statement = `mutation CreatePriceList($input: CreatePriceListInput!) {
      createPriceList(input: $input) {${ITEM_FIELDS}}
    }`;
    const response = (await API.graphql(
      graphqlOperation(statement, { input })
    )) as any;
    return response.data.createPriceList;
  }

  async updatePriceList(
    input: Partial<PriceListModel> & { id: string }
  ): Promise<PriceListModel> {
    const statement = `mutation UpdatePriceList($input: UpdatePriceListInput!) {
      updatePriceList(input: $input) {${ITEM_FIELDS}}
    }`;
    const response = (await API.graphql(
      graphqlOperation(statement, { input })
    )) as any;
    return response.data.updatePriceList;
  }

  async deletePriceList(id: string): Promise<void> {
    const statement = `mutation DeletePriceList($input: DeletePriceListInput!) {
      deletePriceList(input: $input) {
        id
      }
    }`;
    await API.graphql(graphqlOperation(statement, { input: { id } }));
  }

  /**
   * A single chunk of a bulk import.
   *
   * Price lists are far too large to import inside the 30s AppSync resolver
   * timeout, so the caller drives the import by following the returned
   * `phase` / `nextOffset` (see PriceListBulkImportComponent).
   */
  async bulkImportChunk(
    csvUrl: string,
    options: { offset?: number; limit?: number; replace?: boolean } = {}
  ): Promise<PriceListImportProgress> {
    const statement = `mutation BulkPriceListImport($input: ModelBulkPriceListImportInput!) {
      bulkPriceListImport(input: $input) {
        result
        message
        phase
        totalRows
        processedRows
        insertedRows
        failedRows
        deletedRows
        nextOffset
        done
      }
    }`;
    const input: any = { csvUrl };
    if (options.offset !== undefined) input.offset = options.offset;
    if (options.limit !== undefined) input.limit = options.limit;
    if (options.replace !== undefined) input.replace = options.replace;

    const response = (await API.graphql(
      graphqlOperation(statement, { input })
    )) as any;
    return response?.data?.bulkPriceListImport ?? { result: 'FAILED' };
  }

  async initializeData(clientID: string): Promise<void> {
    const data = await this.listByClientID(clientID, undefined, 50).catch(
      (err) => {
        console.log(err);
        return { items: [], nextToken: null };
      }
    );
    this.dataStore.items = data.items;
    this.PRICE_LIST_DATA.next([...this.dataStore.items]);
    this.nextToken = (data as any).nextToken || undefined;
  }

  async filterItems(clientID: string, keyword: string): Promise<void> {
    this.dataStore.items = [];
    // Capped, unlike the chart-of-accounts equivalent: a price list can hold
    // tens of thousands of rows and a two-character search would otherwise pull
    // all of them into the table.
    const data: any = await this.listByClientID(
      clientID,
      { searchKeyword: { contains: keyword } },
      500
    ).catch((err) => {
      console.log(err);
      return null;
    });
    if (data) {
      this.dataStore.items = data.items;
      this.nextToken = data.nextToken || undefined;
    }
    this.PRICE_LIST_DATA.next([...this.dataStore.items]);
  }

  async fetchAdditional(clientID: string): Promise<void> {
    if (!this.nextToken) return;
    const data = await this.listByClientID(
      clientID,
      undefined,
      150,
      this.nextToken
    ).catch((err) => {
      console.log(err);
      return { items: [], nextToken: null };
    });
    this.dataStore.items = this.dataStore.items.concat(data.items);
    this.PRICE_LIST_DATA.next([...this.dataStore.items]);
    this.nextToken = (data as any).nextToken || undefined;
  }

  /** Lowercase, space-free blob the table's search box filters on. */
  private buildSearchKeyword(item: PriceListModel): string {
    return `${item.productCode || ''}${item.barcode || ''}${item.productName || ''}`
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  async addItem(item: PriceListModel): Promise<void> {
    const user = await Auth.currentAuthenticatedUser();
    const input: PriceListModel = {
      clientID: user?.attributes.sub,
      productCode: item.productCode || '',
      barcode: item.barcode || '',
      productName: item.productName,
      unit: item.unit || '',
      retailPrice: Number(item.retailPrice),
      searchKeyword: this.buildSearchKeyword(item),
    };
    const created = await this.createPriceList(input);
    this.dataStore.items.unshift(created);
    this.PRICE_LIST_DATA.next([...this.dataStore.items]);
  }

  async editItem(itemData: PriceListModel): Promise<void> {
    const found = this.dataStore.items.find((i) => i.id === itemData.id);
    if (found) {
      const updated = await this.updatePriceList({
        id: itemData.id!,
        productCode: itemData.productCode || '',
        barcode: itemData.barcode || '',
        productName: itemData.productName,
        unit: itemData.unit || '',
        retailPrice: Number(itemData.retailPrice),
        searchKeyword: this.buildSearchKeyword(itemData),
      });
      Object.assign(found, updated);
      this.PRICE_LIST_DATA.next([...this.dataStore.items]);
      this.snackBar.open('Changes successfully saved', '', {
        duration: 5000,
        panelClass: ['white-snackbar'],
      });
    }
  }

  async removeItem(item: PriceListModel): Promise<void> {
    const found = this.dataStore.items.find((i) => i.id === item.id);
    if (found) {
      this.dataStore.items = this.dataStore.items.filter(
        (i) => i.id !== item.id
      );
      this.PRICE_LIST_DATA.next([...this.dataStore.items]);
      await this.deletePriceList(item.id!).catch((err) => console.log(err));
      this.snackBar.open(`The "${item.productName}" item has been deleted`, '', {
        duration: 5000,
        panelClass: ['white-snackbar'],
      });
    }
  }
}
